/**
 * Districts Index Page — Route Module
 *
 * The programmatic-SEO hub index: the 18 official districts of Hong Kong,
 * grouped under their region headings, each card linking down to its district
 * hub. Every string on this page is DATA (backend-localized district / region
 * names) or an existing catalogued count string — the page adds no new UI copy.
 */
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { Link } from '../../../../shared/i18n/routing';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import type { loader } from './index.loader';

export function Component() {
  const { regions, seo } = useLoaderData<typeof loader>();
  const t = useTranslations('estates');

  return (
    <>
      <SeoHead {...seo} />
      <div className="min-h-screen bg-muted">
        <div className="bg-foreground py-16">
          <PageContainer size="7xl" className="sm:px-6 lg:px-8">
            {/* The H1 IS the server-computed SEO title (already localized and
                length-budgeted by the backend), so the indexed <title> and the
                rendered heading are provably the same string. */}
            <h1 className="text-4xl font-bold text-background">{seo.title}</h1>
            {seo.description && (
              <p className="mt-2 max-w-3xl text-lg text-background/70">{seo.description}</p>
            )}
          </PageContainer>
        </div>

        <PageContainer size="7xl" className="space-y-12 py-12 sm:px-6 lg:px-8">
          {regions.map((region) => (
            <section key={region.regionSlug}>
              <h2 className="mb-6 text-2xl font-bold text-foreground">
                {plaintextString(region.name)}
              </h2>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {region.districts.map((district) => (
                  <Link
                    key={district.slug}
                    href={routes.districts.detail(district.slug)}
                    prefetch="intent"
                    className="group rounded-xl bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <h3 className="font-semibold text-foreground transition-colors group-hover:text-success-text">
                      {plaintextString(district.shortName)}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {plaintextString(district.name)}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{t('showing', { count: district.estateCount })}</span>
                      <span className="text-success-text">
                        {t('detail.listingCount', { count: district.activeListingCount })}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </PageContainer>
      </div>
    </>
  );
}
